import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from '../api/api.service';

export interface Area {
  title: string;
  desc: string;
}

export enum FeedbackType {
  Positive = 'positive',
  Negative = 'negative',
  Neutral = 'neutral'
}

export interface ReferenceSummaryResponse {
  data: {
    reference_summary: {
      id: number;
      summary: string;
      ref_count: number;
      areas_of_strength: Array<{title: string, description: string}>;
      areas_of_improvement: Array<{title: string, description: string}>;
      areas_of_note: Array<{title: string, description: string}>;
    };
    user_feedback: {
      reference_summary: number;
      user: number;
      positive_feedback: boolean;
    } | null;
  };
  status_message: string;
  message: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class ReferenceSummaryService {

  constructor(
    private apiService: ApiService
  ) { }

  /**
   * Get reference summary for a specific check, candidate and referee
   * @param checkId - The check ID
   * @param candidateId - The candidate ID
   * @param refereeId - The referee ID
   * @returns Observable of ReferenceSummaryResponse
   */
  getReferenceSummary(checkId: number, candidateId: number, refereeId: number): Observable<ReferenceSummaryResponse> {
    const path = `check/references/check/${checkId}/summary`;
    const params = { candidate_id: candidateId, referee_id: refereeId };
    return this.apiService.get(path, { params });
  }

  /**
   * Get candidate summary for a specific check and candidate (without specific referee)
   * @param checkId - The check ID
   * @param candidateId - The candidate ID
   * @returns Observable of ReferenceSummaryResponse
   */
  getCandidateSummary(checkId: number, candidateId: number): Observable<ReferenceSummaryResponse> {
    const path = `check/references/check/${checkId}/summary`;
    const params = { candidate_id: candidateId };
    return this.apiService.get(path, { params });
  }

  /**
   * Transform API response to match component interface
   * @param response - The API response
   * @returns Transformed data for the component
   */
  transformResponseForComponent(response: ReferenceSummaryResponse) {
    // Map feedback from boolean to enum
    let feedback = FeedbackType.Neutral;
    if (response.data.user_feedback && response.data.user_feedback.positive_feedback !== undefined) {
      feedback = response.data.user_feedback.positive_feedback ? FeedbackType.Positive : FeedbackType.Negative;
    }

    return {
      id: response.data.reference_summary.id,
      summary: response.data.reference_summary.summary,
      refereeCount: response.data.reference_summary.ref_count,
      areasOfStrength: response.data.reference_summary.areas_of_strength.map(item => ({
        title: item.title,
        desc: item.description
      })),
      areasOfImprovement: response.data.reference_summary.areas_of_improvement.map(item => ({
        title: item.title,
        desc: item.description
      })),
      areasOfNote: response.data.reference_summary.areas_of_note.map(item => ({
        title: item.title,
        desc: item.description
      })),
      feedback: feedback
    };
  }

  /**
   * Extract reference summary data from check object (without user feedback)
   * @param check - The check object containing reference_summary
   * @returns Transformed data for the component or null if feature flag is disabled
   */
  extractReferenceSummaryFromCheck(check: any) {
    if (!check?.reference_summary) {
      return null;
    }

    const referenceSummary = check.reference_summary;

    // Extract user feedback from check data
    let feedback = FeedbackType.Neutral;
    if (check.user_feedback && check.user_feedback.positive_feedback !== undefined) {
      feedback = check.user_feedback.positive_feedback ? FeedbackType.Positive : FeedbackType.Negative;
    }

    return {
      id: referenceSummary.id,
      summary: referenceSummary.summary,
      refereeCount: referenceSummary.ref_count,
      areasOfStrength: referenceSummary.areas_of_strength.map(item => ({
        title: item.title,
        desc: item.description
      })),
      areasOfImprovement: referenceSummary.areas_of_improvement.map(item => ({
        title: item.title,
        desc: item.description
      })),
      areasOfNote: referenceSummary.areas_of_note.map(item => ({
        title: item.title,
        desc: item.description
      })),
      feedback: feedback
    };
  }

  /**
   * Set user feedback for reference summary
   * @param checkId - The check ID
   * @param referenceSummaryId - The reference summary ID
   * @param userId - The user ID (can be string UUID or number)
   * @param feedback - The feedback type
   * @returns Observable of the response
   */
  setUserFeedback(checkId: number, referenceSummaryId: number, userId: string | number, feedback: FeedbackType): Observable<any> {
    const path = `check/references/check/${checkId}/summary/feedback`;
    const payload = {
      reference_summary: referenceSummaryId,
      user: userId,
      positive_feedback: feedback === FeedbackType.Positive
    };

    return this.apiService.put(path, payload);
  }
}
